// Catches a PDF that nothing can read, at the moment it's chosen, so every
// PDF tool can say so by name -- instead of each one failing later with its
// own generic "may be corrupted or password-protected" message and no
// indication of which file (or why). Confirmed as a real problem, not
// assumed: Merge PDF's QA report found a truncated file brought down the
// whole operation with "Failed to merge PDFs", naming nothing.
//
// Deliberately conservative -- a file is only reported unreadable when ALL
// of these hold, so nothing a tool could still use is ever turned away:
//   1. it looks structurally broken (no %PDF- header, or no %%EOF near the
//      end -- the signature of a truncated download), AND
//   2. pdf-lib can't open it, AND
//   3. pdf.js (which recovers from far more damage) can't open it either.
// A cheap check first means a normal PDF costs two tiny slice reads; the
// heavy parsers only load when a file already looks wrong.

export async function looksDamaged(file: File): Promise<boolean> {
  const decoder = new TextDecoder("latin1");
  const head = decoder.decode(await file.slice(0, 1024).arrayBuffer());
  if (!head.includes("%PDF-")) return true;
  const tail = decoder.decode(await file.slice(Math.max(0, file.size - 8192)).arrayBuffer());
  return !tail.includes("%%EOF");
}

async function pdfLibCanOpen(file: File): Promise<boolean> {
  try {
    const { PDFDocument } = await import("pdf-lib");
    const doc = await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: true, updateMetadata: false });
    // "Loaded" isn't "readable": found on the live site, a file truncated
    // mid-download loaded without error but had no page tree at all
    // (getPageCount throws), and Split PDF accepted it as "0 pages". Only
    // a document that actually has pages counts as openable.
    return doc.getPageCount() > 0;
  } catch {
    return false;
  }
}

async function pdfJsCanOpen(file: File): Promise<boolean> {
  try {
    const { loadPdfDocument } = await import("./utils");
    const doc = await loadPdfDocument(file);
    try {
      return doc.numPages > 0;
    } finally {
      void doc.loadingTask.destroy();
    }
  } catch {
    return false;
  }
}

export async function isUnreadablePdf(file: File): Promise<boolean> {
  if (!(await looksDamaged(file))) return false;
  if (await pdfLibCanOpen(file)) return false;
  if (await pdfJsCanOpen(file)) return false;
  return true;
}
